/**
 * The one HTTP core behind every admin API call (SPRITE-1468).
 *
 * `utils/api.ts` (react-query hooks) and `utils/adminApi.ts` (page-level
 * calls) used to carry their own fetch wrappers with different error
 * classes, timeouts and retry rules. Both now call `adminRequest`, so auth,
 * timeout, 401 retry and error shape are decided once.
 */
import { auth } from '@/config/firebase';

const apiUrl =
  import.meta.env.VITE_ADMIN_API_URL || import.meta.env.VITE_API_URL;
if (!apiUrl) {
  throw new Error(
    'Required environment variable VITE_ADMIN_API_URL or VITE_API_URL is not set'
  );
}

/** Admin service base URL: direct when VITE_ADMIN_API_URL is set, else via the gateway. */
export const ADMIN_API_URL: string =
  import.meta.env.VITE_ADMIN_API_URL || `${apiUrl}/api/admin`;

const REQUEST_TIMEOUT_MS = 30_000;

/**
 * A failed admin API call. `status` is the HTTP status (0 for a network
 * failure, 408 for a client-side timeout); `problems` lists each check a
 * refused write failed (the model catalogue's gates return them all at once).
 */
export class AdminApiError extends Error {
  readonly status: number;
  readonly problems: string[];
  readonly response?: unknown;

  constructor(
    message: string,
    status: number,
    options: { problems?: string[]; response?: unknown } = {}
  ) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
    this.problems = options.problems ?? [];
    this.response = options.response;
  }
}

/** Status → copy safe to show an admin. Never the raw server message for 5xx. */
export function friendlyApiMessage(error: unknown, fallback: string): string {
  if (!(error instanceof AdminApiError)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }
  switch (error.status) {
    case 0:
      return 'Network error. Check your connection and try again.';
    case 400:
    case 409:
      return error.message || fallback;
    case 401:
      return 'Your session has expired. Sign in again.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'Not found. It may have been deleted.';
    case 408:
      return 'The request timed out. Try again.';
    case 429:
      return 'Too many requests. Wait a moment and try again.';
    default:
      return fallback;
  }
}

/**
 * Resolve the bearer token: the caller's explicit token, else the signed-in
 * Firebase user's ID token (force-refreshed on retry).
 * @param explicit - token supplied by the caller, if any
 * @param forceRefresh - bypass Firebase's cached token
 */
async function resolveToken(
  explicit: string | undefined,
  forceRefresh: boolean
): Promise<string> {
  if (explicit && !forceRefresh) return explicit;
  const user = auth.currentUser;
  if (!user) {
    throw new AdminApiError('Not authenticated', 401);
  }
  try {
    return await user.getIdToken(forceRefresh);
  } catch {
    throw new AdminApiError('Failed to get authentication token', 401);
  }
}

export interface AdminRequestOptions extends RequestInit {
  /** Bearer token to send; defaults to the signed-in user's ID token. */
  token?: string;
}

/**
 * Perform one authenticated request against the admin service and return the
 * raw Response. A 401 is retried once with a force-refreshed token.
 * @param endpoint - path under ADMIN_API_URL, e.g. `/users?page=2`
 */
export async function adminRequestRaw(
  endpoint: string,
  options: AdminRequestOptions = {},
  attempt = 0
): Promise<Response> {
  const { token: explicitToken, headers, ...init } = options;
  const token = await resolveToken(explicitToken, attempt > 0);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${ADMIN_API_URL}${endpoint}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(headers as Record<string, string> | undefined),
      },
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new AdminApiError('Request timeout - please try again', 408);
    }
    throw new AdminApiError('Network error - please check your connection', 0);
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 401 && attempt === 0) {
    return adminRequestRaw(endpoint, options, 1);
  }

  if (!response.ok) {
    const body: Record<string, unknown> = await response
      .json()
      .catch(() => ({}));
    const message =
      (typeof body.message === 'string' && body.message) ||
      (typeof body.error === 'string' && body.error) ||
      `HTTP ${response.status}: ${response.statusText}`;
    throw new AdminApiError(message, response.status, {
      problems: Array.isArray(body.problems) ? (body.problems as string[]) : [],
      response: body,
    });
  }

  return response;
}

/**
 * Perform one authenticated JSON request against the admin service.
 * @param endpoint - path under ADMIN_API_URL
 */
export async function adminRequest<T = unknown>(
  endpoint: string,
  options: AdminRequestOptions = {}
): Promise<T> {
  const response = await adminRequestRaw(endpoint, options);
  return response.json() as Promise<T>;
}
